import React, { useMemo } from "react";
import { Box, Card, CircularProgress, Chip, Grid, Typography, Button, alpha } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import placeholderImage from "../../../Images/fav.png";
import Barcode from "react-barcode";
import {
  useGetCategoryQuery,
  useGetProductBrandQuery,
  useGetProductQuery,
  useGetProductTagQuery,
} from "../../../Api/attoDeskApi";
import { ICategory, IProduct, IProductBrand, IProductTag } from "../../../Api/Interface/api.interface";

// ── Constants ────────────────────────────────────────────────────────────────
const BRAND  = "#2f3891";
const BORDER = "#e2e8f0";

const StatusBadge = ({ active, label }: { active: boolean; label: string }) => (
  <Box sx={{
    display: "inline-flex", alignItems: "center", gap: 0.75,
    px: 1.5, py: 0.5, borderRadius: 2,
    fontSize: 12, fontWeight: 700,
    color: active ? "#16a34a" : "#94a3b8",
    bgcolor: active ? "#f0fdf4" : "#f8fafc",
    border: `1px solid ${active ? "#bbf7d0" : BORDER}`,
  }}>
    {active ? <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> : <CancelOutlinedIcon sx={{ fontSize: 14 }} />}
    {label}
  </Box>
);

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", py: 1.25, borderBottom: "1px solid #f1f5f9" }}>
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", flexShrink: 0, mr: 2 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a", textAlign: "right" }}>{value || "N/A"}</Typography>
  </Box>
);

const SectionCard = ({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) => (
  <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, overflow: "hidden" }}>
    <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: alpha(BRAND, 0.08), display: "flex", alignItems: "center", justifyContent: "center" }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Card>
);

// ── Main Component ───────────────────────────────────────────────────────────
const ProductDetail = () => {
  const navigate = useNavigate();
  const { id: ProductId } = useParams<{ id: string }>();
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;

  const { data, isLoading } = useGetProductQuery({
    searchText: "", id: String(ProductId),
    productBrandId: null, productCategoryId: null, productTagIds: null,
    productViewOnline: null, createdDateStart: null, createdDateEnd: null, createdDate: null,
  });
  const { data: brandData }    = useGetProductBrandQuery({ searchText: "", isActive: true });
  const { data: tagData }      = useGetProductTagQuery({ searchText: "", isActive: true });
  const { data: categoryData } = useGetCategoryQuery();

  const product = useMemo(() => {
    if (data && Array.isArray(data.data) && data.data.length > 0) return data.data[0] as IProduct;
    return null;
  }, [data]);

  const brandMap = useMemo(() => {
    const map = new Map<string | number, string>();
    (brandData?.data as IProductBrand[] | undefined)?.forEach(b => map.set(b.id, b.productBrandName));
    return map;
  }, [brandData]);

  const tagMap = useMemo(() => {
    const map = new Map<string | number, string>();
    (tagData?.data as IProductTag[] | undefined)?.forEach(t => map.set(t.id, t.tagName));
    return map;
  }, [tagData]);

  const categoryMap = useMemo(() => {
    const map = new Map<string | number, string>();
    (categoryData?.data as ICategory[] | undefined)?.forEach(c => map.set(c.id, c.categoryName));
    return map;
  }, [categoryData]);

  if (isLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "40vh" }}><CircularProgress /></Box>;
  }

  if (!product) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#64748b" }}>Product not found</Typography>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2, textTransform: "none" }}>Go Back</Button>
      </Box>
    );
  }

  const imgSrc = product.productImg ? `${LARAVEL_API_URL}/${product.productImg}` : placeholderImage;
  const categoryName = categoryMap.get(product.productCategoryId) || "N/A";
  const brandName = brandMap.get(product.productBrandId) || "N/A";
  const tags = (product.productTagIds || []).map(id => tagMap.get(id)).filter(Boolean);

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto" }}>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>{product.productName}</Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.25 }}>{product.productShortDescription || "Bus & Service Details"}</Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={<ArrowBackIosIcon sx={{ fontSize: 12 }} />}
          onClick={() => navigate(-1)}
          sx={{ textTransform: "none", fontWeight: 600, fontSize: 12, borderRadius: 2, borderColor: BORDER, color: "#475569" }}>
          Back
        </Button>
      </Box>

      <Grid container spacing={2.5}>
        {/* ── LEFT COLUMN ── */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Image */}
            <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, overflow: "hidden" }}>
              <Box sx={{
                position: "relative", width: "100%", aspectRatio: "1/1",
                bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Box component="img" src={imgSrc} alt={product.productName}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.src = placeholderImage; }}
                  sx={{ maxWidth: "85%", maxHeight: "85%", objectFit: "contain", borderRadius: 2 }} />
                {!product.productImg && (
                  <Typography sx={{ position: "absolute", bottom: 12, fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>No Image Available</Typography>
                )}
              </Box>
            </Card>

            {/* Barcode */}
            {product.productBarcode && (
              <SectionCard title="Barcode" icon={<DescriptionOutlinedIcon sx={{ fontSize: 15, color: BRAND }} />}>
                <Box sx={{ textAlign: "center" }}>
                  <Barcode value={product.productBarcode} width={1.5} height={50} fontSize={12} margin={0} />
                  <Typography sx={{ fontSize: 11, color: "#64748b", mt: 1, fontFamily: "monospace", letterSpacing: 1 }}>
                    {product.productBarcode}
                  </Typography>
                </Box>
              </SectionCard>
            )}

            {/* Button Color */}
            {product.productButtonColor && (
              <SectionCard title="Display Color" icon={<Box sx={{ width: 15, height: 15, borderRadius: 1, bgcolor: product.productButtonColor }} />}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: product.productButtonColor, border: `1px solid ${BORDER}` }} />
                  <Typography sx={{ fontSize: 12, fontFamily: "monospace", color: "#64748b" }}>{product.productButtonColor}</Typography>
                </Box>
              </SectionCard>
            )}
          </Box>
        </Grid>

        {/* ── RIGHT COLUMN ── */}
        <Grid item xs={12} md={8}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Status badges */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <StatusBadge active={!!product.productCanSell} label="Active" />
              <StatusBadge active={!!product.productViewOnline} label="View Online" />
              <StatusBadge active={!!product.productDetailsIsLooping} label="Looping" />
            </Box>

            {/* Product Info */}
            <SectionCard title="Product Information" icon={<DirectionsBusIcon sx={{ fontSize: 15, color: BRAND }} />}>
              <DetailRow label="Name" value={product.productName} />
              <DetailRow label="Category" value={categoryName} />
              <DetailRow label="Brand" value={brandName} />
              <DetailRow label="Price" value={product.productPrice ? `$${Number(product.productPrice).toFixed(2)}` : "N/A"} />
              <DetailRow label="Tags" value={
                tags.length > 0
                  ? <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      {tags.map((t, i) => <Chip key={i} label={t} size="small" sx={{ fontSize: 11, fontWeight: 600, bgcolor: alpha(BRAND, 0.08), color: BRAND, height: 22 }} />)}
                    </Box>
                  : "No tags"
              } />
              <DetailRow label="Short Description" value={product.productShortDescription || "N/A"} />
            </SectionCard>

            {/* Long Description */}
            <SectionCard title="Description" icon={<DescriptionOutlinedIcon sx={{ fontSize: 15, color: BRAND }} />}>
              <Typography sx={{ fontSize: 13, color: "#374151", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                {product.productLongDescription || "No description available."}
              </Typography>
            </SectionCard>

            {/* Looping details */}
            {product.productDetailsIsLooping && product.loopingDetails && (
              <SectionCard title={`Looping: ${product.loopingDetails.shortTableName || "Details"}`} icon={<CategoryOutlinedIcon sx={{ fontSize: 15, color: BRAND }} />}>
                {product.loopingDetails.tableColumns?.map((col, i) => (
                  <DetailRow key={i} label={col.name}
                    value={col.type === "boolean" ? (col.value ? "Yes" : "No") : (col.value ?? "N/A")} />
                ))}
              </SectionCard>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductDetail;
